"use client";

import React, { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { usePrevNextButtons } from "../../../components/EmblaCarouselControls/EmblaCarouselControls";
import { arrowGlyphShape, arrowStyle, isBareArrow } from "../arrowStyle";
import { ArrowGlyph } from "./ArrowGlyph";
import { CarouselIndicator } from "./CarouselIndicator";
import {
  ArrowButton,
  BottomNav,
  CarouselRoot,
  CarouselSlide,
  CarouselTrack,
  CarouselViewport,
  HeaderNav,
} from "../DynamicHighlights.styles";
import {
  HIGHLIGHT_PEEK,
  type HighlightsArrowType,
  type HighlightsCarouselWidth,
  type HighlightsIndicatorType,
  type HighlightsNavigation,
  type HighlightItem,
} from "../types";
import { ARIA_NEXT, ARIA_PREV } from "../strings";

/** Suavidade da transição do embla (o default é 25; a spec pede 25–30). */
const EMBLA_DURATION = 28;

export type HighlightsCarouselProps = {
  items: HighlightItem[];
  /** Itens por página no desktop (1–3). No mobile é sempre 1. */
  itemsPerPage: number;
  gap: number;
  isMobile: boolean;
  loop: boolean;
  /** `full` faz o trilho vazar pela direita da viewport. */
  carouselWidth: HighlightsCarouselWidth;
  autoplay: boolean;
  /** Intervalo do autoplay em ms. */
  autoplaySpeed: number;
  navigation: HighlightsNavigation;
  arrowType: HighlightsArrowType;
  indicatorType: HighlightsIndicatorType;
  navigationColor: string;
  /** Alinhamento do cabeçalho: as setas e o indicador acompanham. */
  align: "start" | "center";
  /**
   * Renderiza o CABEÇALHO do bloco. Recebe as setas prontas para montá-las na
   * mesma linha do título (padrão do bloco 38) — `null` quando não há setas ou
   * quando o alinhamento é centralizado, caso em que elas descem para o rodapé
   * daqui.
   */
  renderHeader?: (arrows: React.ReactNode | null) => React.ReactNode;
  /** `single` avisa que há um item por página — o card usa proporção larga. */
  renderItem: (item: HighlightItem, single: boolean) => React.ReactNode;
};

export const HighlightsCarousel = ({
  items,
  itemsPerPage,
  gap,
  isMobile,
  loop,
  carouselWidth,
  autoplay,
  autoplaySpeed,
  navigation,
  arrowType,
  indicatorType,
  navigationColor,
  align,
  renderHeader,
  renderItem,
}: HighlightsCarouselProps) => {
  const fullBleed = carouselWidth === "full";
  const perView = isMobile ? 1 : Math.min(Math.max(itemsPerPage, 1), 3);

  /*
   * Espia e repetição infinita são EXCLUDENTES no embla: com o trilho sempre
   * "sobrando" um pedaço, o ponto de emenda do loop salta.
   *
   * Quem decide é o Champion: com o loop desligado (default) vale o espia, que
   * dá o trilho contínuo das referências; ligando o loop, o espia sai e a
   * repetição funciona de verdade. O switch antes era ignorado — existia na
   * sidebar e não fazia nada, que é pior do que não existir.
   */
  const peek = loop ? 0 : HIGHLIGHT_PEEK;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    loop,
    duration: EMBLA_DURATION,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setSnapCount(emblaApi.scrollSnapList().length);
    };
    update();
    emblaApi.on("select", update).on("reInit", update);
  }, [emblaApi]);

  // Reinicializa quando o que define a geometria do trilho muda.
  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, perView, gap, items.length, fullBleed, loop]);

  const [isPaused, setIsPaused] = useState(false);

  /*
   * Autoplay por `setInterval` + `scrollNext` (padrão do pacote — ver
   * `components/Gallery/Carousel`), sem dependência nova. Como o espia desliga
   * o loop do embla, ao chegar no fim voltamos ao início manualmente.
   *
   * Pausa no hover/foco: um trilho que anda sozinho enquanto a pessoa lê um
   * card é hostil, e sem isso o teclado nunca alcança o conteúdo.
   */
  useEffect(() => {
    if (!emblaApi || !autoplay || isPaused) return;
    if (items.length <= perView) return;

    const interval = setInterval(() => {
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else emblaApi.scrollTo(0);
    }, Math.max(autoplaySpeed, 1000));

    return () => clearInterval(interval);
  }, [emblaApi, autoplay, autoplaySpeed, isPaused, items.length, perView]);

  const showArrows =
    (navigation === "arrows" || navigation === "arrowsAndIndicator") &&
    items.length > perView;
  const showIndicator =
    (navigation === "indicator" || navigation === "arrowsAndIndicator") &&
    items.length > perView;

  /*
   * Espia: o slide ocupa (1 - espia)/n da referência, e o gap é descontado do
   * padding-left. Sai como FRAÇÃO, não como porcentagem pronta, porque quem
   * decide a referência é o styled: no modo contido ela é o viewport (que já é
   * a coluna), no full-bleed é a largura da coluna — o viewport ali está
   * esticado até a borda da tela e mediria um card maior que a coluna inteira.
   */
  const slideBasis = `${(1 - peek) / perView}`;

  const bare = isBareArrow(arrowType);

  const arrowButtons = (
    <>
      <ArrowButton
        type="button"
        aria-label={ARIA_PREV}
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
        $bare={bare}
        style={arrowStyle(arrowType, navigationColor)}
      >
        <ArrowGlyph shape={arrowGlyphShape(arrowType)} />
      </ArrowButton>
      <ArrowButton
        type="button"
        aria-label={ARIA_NEXT}
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
        $bare={bare}
        style={arrowStyle(arrowType, navigationColor)}
      >
        <ArrowGlyph shape={arrowGlyphShape(arrowType)} flip />
      </ArrowButton>
    </>
  );

  /*
   * Padrão do bloco 38: com o cabeçalho à esquerda as setas vão para a linha do
   * título (`HeaderNav`, que some no mobile); centralizado, elas não entram no
   * cabeçalho. O `BottomNav` é renderizado sempre que há setas e o CSS decide
   * quando mostrá-lo — centralizado, ou no mobile, onde o HeaderNav sumiu.
   */
  const headerArrows =
    showArrows && align !== "center" ? <HeaderNav $bare={bare}>{arrowButtons}</HeaderNav> : null;

  return (
    <CarouselRoot
      data-testid="dynamic-highlights-carousel"
      onMouseEnter={autoplay ? () => setIsPaused(true) : undefined}
      onMouseLeave={autoplay ? () => setIsPaused(false) : undefined}
      onFocusCapture={autoplay ? () => setIsPaused(true) : undefined}
      onBlurCapture={autoplay ? () => setIsPaused(false) : undefined}
    >
      {renderHeader?.(headerArrows)}

      <CarouselViewport ref={emblaRef} $fullBleed={fullBleed} $loop={loop}>
        <CarouselTrack $gap={gap}>
          {items.map((item) => (
            <CarouselSlide key={item.id} $basis={slideBasis} $gap={gap} $fullBleed={fullBleed}>
              {renderItem(item, perView === 1)}
            </CarouselSlide>
          ))}
        </CarouselTrack>
      </CarouselViewport>

      {showIndicator && (
        <CarouselIndicator
          type={indicatorType}
          count={snapCount}
          index={selectedIndex}
          color={navigationColor}
          align={align}
          onGoTo={(target) => emblaApi?.scrollTo(target)}
        />
      )}

      {showArrows && <BottomNav $align={align} $bare={bare}>{arrowButtons}</BottomNav>}
    </CarouselRoot>
  );
};
